import { Table } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Breadcrumbs from "../../../../components/Breadcrumbs";

import { fetchCandidates, updateCandidate, registerCandidate, fetchDepartments } from "../../../../services/recruitmentService";
import { encrypt } from "../../../../utils/crypto";
import TooltipWrapper from "../../../../utils/TooltipWrapper";
import CandidateStatusBadge from "./components/CandidateStatusBadge";
import CandidateModal from "./components/CandidateModal";

const candidateStatusOptions = [
  { label: "Registered", value: "REGISTERED" },
  { label: "Shortlisted", value: "SHORTLISTED" },
  { label: "Interview Scheduled", value: "INTERVIEW_SCHEDULED" },
  { label: "Selected", value: "SELECTED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "On Hold", value: "ON_HOLD" },
];

const CandidateList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 10,
    search: null,
    status: null,
    applied_position: null,
    is_blacklisted: null,
    date_from: null,
    date_to: null,
    department_id: null,
    sortBy: 'created_at',
    order: 'DESC',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const fetchCandidatesList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        per_page: filters.pageSize,
      };

      // Add all filter parameters except pagination
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && key !== 'page' && key !== 'pageSize') {
          params[key] = filters[key];
        }
      });

      console.log('Fetching candidates with params:', params);
      const response = await fetchCandidates(params);

      if (response?.success) {
        setCandidates(response.result || []);
        
        // Handle pagination response structure
        if (response.pagination) {
          console.log('Setting pagination from response:', response.pagination);
          setPagination({
            current_page: filters.page,
            per_page: filters.pageSize,
            total: response.pagination.total || 0,
            total_pages: response.pagination.total_pages || Math.ceil((response.pagination.total || 0) / filters.pageSize)
          });
        } else {
          // Fallback for different response structure
          const total = response.total || response.count || 0;
          console.log('Setting fallback pagination, total:', total);
          setPagination({
            current_page: filters.page,
            per_page: filters.pageSize,
            total: total,
            total_pages: Math.ceil(total / filters.pageSize)
          });
        }
      } else {
        console.error('API response not successful:', response);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
      toast.error(t("Failed to fetch candidates"));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchCandidatesList();
  }, [fetchCandidatesList]);

  // Fetch departments and HR list for filters
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const deptResponse = await fetchDepartments();
        
        if (deptResponse?.success) {
          setDepartments(deptResponse.result || []);
        }
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };
    
    fetchFilterData();
  }, []);



  const updateFilters = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to first page when filters change
    }));
  };

  const updatePagination = (page, pageSize) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        page: parseInt(page) || 1,
        pageSize: pageSize || prev.pageSize,
      };
      return newFilters;
    });
  };



  const handleView = (record) => {
    if (!record?.id) return;
    const encryptedId = encrypt(record.id.toString());
    navigate(`${encryptedId}`);
  };

  const handleEdit = (record) => {
    if (!record?.id) return;
    setSelectedCandidate(record);
    setModalOpen(true);
  };

  const handleNewCandidate = () => {
    setSelectedCandidate(null); // null indicates new candidate mode
    setModalOpen(true);
  };

  const handleModalSave = async (formData) => {
    setModalSubmitting(true);
    try {
      let response;
      if (selectedCandidate) {
        // Edit existing candidate
        response = await updateCandidate(selectedCandidate.id, formData);
        toast.success(response?.message || t("Candidate updated successfully"));
      } else {
        // Register new candidate
        response = await registerCandidate(formData);
        toast.success(response?.message || t("Candidate registered successfully"));
      }
      
      setModalOpen(false);
      setSelectedCandidate(null);
      fetchCandidatesList(); // Refresh the list
    } catch (error) {
      const message = error?.response?.data?.message || 
        (selectedCandidate ? t("Unable to update candidate") : t("Unable to register candidate"));
      toast.error(message);
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCandidate(null);
  };

  const columns = [
    {
      title: t("Candidate Code"),
      dataIndex: "candidate_code",
      key: "candidate_code",
      width: 120,
    },
    {
      title: t("Candidate"),
      dataIndex: "full_name",
      key: "full_name",
      render: (text, record) => (
        <div>
          <div className="fw-semibold">{record?.full_name}</div>
          <div className="text-muted small">{record?.email}</div>
        </div>
      ),
    },
    {
      title: t("Phone"),
      dataIndex: "phone",
      key: "phone",
      render: (phone, record) => (
        <div>
          <div>{phone}</div>
          {record.alternate_phone && (
            <div className="text-muted small">
              Alt: {record.alternate_phone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t("Experience"),
      dataIndex: "experience_years",
      key: "experience_years",
      render: (value) => (
        <span>
          {value || 0} {t("yrs")}
        </span>
      ),
    },
    {
      title: t("Status"),
      dataIndex: "current_status",
      key: "current_status",
      render: (status) => <CandidateStatusBadge status={status} />,
    },
    {
      title: t("Registered On"),
      dataIndex: "created_at",
      key: "created_at",
      render: (timestamp) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to milliseconds
        return date.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div className="btn-group" role="group">
          <TooltipWrapper tooltipText={t("View Details")}>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleView(record)}
              type="button"
            >
              <i className="fa fa-eye" />
            </button>
          </TooltipWrapper>
          <TooltipWrapper tooltipText={t("Edit Candidate")}>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleEdit(record)}
              type="button"
            >
              <i className="fa fa-edit" />
            </button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Recruitment")}
          title={t("Dashboard")}
          subtitle={t("Candidates")}
          name={t("Candidates")}
          page="recruitment"
        />

        <Card>
          <Card.Header className="px-3 pt-3 py-0">
            <div
              className="d-flex flex-md-nowrap flex-wrap justify-content-between align-items-center gap-1 "
              style={{ minHeight: "40px" }}
            >
              <div
                className="d-flex align-items-center gap-2 flex-grow-1"
                style={{ minWidth: 0, overflow: "hidden" }}
              >
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateFilters({
                      search: searchTerm ? searchTerm.trim() : null,
                    });
                  }}
                  className="flex-shrink-1"
                >
                  <InputGroup
                    className=""
                    style={{
                      width: "clamp(200px, 50vw, 20rem)",
                      maxWidth: "100%",
                    }}
                  >
                    <Form.Control
                      className="form-control"
                      placeholder={t("Search by name, email, phone")}
                      style={{
                        height: "2.4rem",
                      }}
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        // Auto-search on change, debounced
                        if (e.target.value.trim() === "") {
                          updateFilters({ search: null });
                        }
                      }}
                    />
                    <Button type="submit" variant="primary">
                      <i className="fa fa-search" />
                    </Button>
                  </InputGroup>
                </Form>
              </div>
              <Button
                variant="primary"
                className="flex-shrink-0 mt-md-0"
                onClick={handleNewCandidate}
              >
                <i className="fa fa-user-plus me-2" />
                <span className="d-none d-sm-inline">
                  {t("Register Candidate")}
                </span>
                <span className="d-inline d-sm-none">{t("Register")}</span>
              </Button>
              
              {/* Advanced Filters Toggle */}
              <Button
                variant="outline-secondary"
                className="flex-shrink-0"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                <i className="fa fa-filter me-2" />
                <span className="d-none d-sm-inline">{t("Filters")}</span>
                <i className={`fa fa-chevron-${showAdvancedFilters ? 'up' : 'down'} ms-2`} />
              </Button>
            </div>
            
            {/* Active Filter Tags */}
            {(filters.status || filters.department_id || filters.applied_position || filters.is_blacklisted || filters.date_from || filters.date_to) && (
              <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                <span className="small text-muted fw-semibold">{t("Active Filters")}:</span>
                
                {filters.status && (
                  <span className="badge bg-primary d-flex align-items-center gap-1">
                    Status: {candidateStatusOptions.find(s => s.value === filters.status)?.label}
                    <button 
                      className="btn-close btn-close-white" 
                      style={{fontSize: '10px'}}
                      onClick={() => updateFilters({ status: null })}
                    ></button>
                  </span>
                )}
                
                {filters.department_id && (
                  <span className="badge bg-info d-flex align-items-center gap-1">
                    Department: {departments.find(d => d.id === filters.department_id)?.department_name}
                    <button 
                      className="btn-close btn-close-white" 
                      style={{fontSize: '10px'}}
                      onClick={() => updateFilters({ department_id: null })}
                    ></button>
                  </span>
                )}
                

                
                {filters.applied_position && (
                  <span className="badge bg-secondary d-flex align-items-center gap-1">
                    Position: {filters.applied_position}
                    <button 
                      className="btn-close btn-close-white" 
                      style={{fontSize: '10px'}}
                      onClick={() => updateFilters({ applied_position: null })}
                    ></button>
                  </span>
                )}
                
                {filters.is_blacklisted && (
                  <span className="badge bg-danger d-flex align-items-center gap-1">
                    {filters.is_blacklisted === 'true' ? 'Blacklisted' : 'Not Blacklisted'}
                    <button 
                      className="btn-close btn-close-white" 
                      style={{fontSize: '10px'}}
                      onClick={() => updateFilters({ is_blacklisted: null })}
                    ></button>
                  </span>
                )}
                
                {(filters.date_from || filters.date_to) && (
                  <span className="badge bg-dark d-flex align-items-center gap-1">
                    Date: {filters.date_from || '...'} to {filters.date_to || '...'}
                    <button 
                      className="btn-close btn-close-white" 
                      style={{fontSize: '10px'}}
                      onClick={() => updateFilters({ date_from: null, date_to: null })}
                    ></button>
                  </span>
                )}
              </div>
            )}

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="border-top pt-3 mt-3">
                <div className="row g-3">
                  {/* Status Filter */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Status")}</Form.Label>
                      <Form.Select
                        value={filters.status || ''}
                        onChange={(e) => updateFilters({ status: e.target.value || null })}
                        size="sm"
                      >
                        <option value="">{t("All Status")}</option>
                        {candidateStatusOptions.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {/* Department Filter */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Department")}</Form.Label>
                      <Form.Select
                        value={filters.department_id || ''}
                        onChange={(e) => updateFilters({ department_id: e.target.value || null })}
                        size="sm"
                      >
                        <option value="">{t("All Departments")}</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.department_name || dept.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>



                  {/* Applied Position */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Applied Position")}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t("Search position")}
                        value={filters.applied_position || ''}
                        onChange={(e) => updateFilters({ applied_position: e.target.value || null })}
                        size="sm"
                      />
                    </Form.Group>
                  </div>

                  {/* Blacklist Status */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Blacklist Status")}</Form.Label>
                      <Form.Select
                        value={filters.is_blacklisted || ''}
                        onChange={(e) => updateFilters({ is_blacklisted: e.target.value || null })}
                        size="sm"
                      >
                        <option value="">{t("All")}</option>
                        <option value="true">{t("Blacklisted")}</option>
                        <option value="false">{t("Not Blacklisted")}</option>
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {/* Date From */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Date From")}</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.date_from || ''}
                        onChange={(e) => updateFilters({ date_from: e.target.value || null })}
                        size="sm"
                      />
                    </Form.Group>
                  </div>

                  {/* Date To */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Date To")}</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.date_to || ''}
                        onChange={(e) => updateFilters({ date_to: e.target.value || null })}
                        size="sm"
                      />
                    </Form.Group>
                  </div>

                  {/* Sort By */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Sort By")}</Form.Label>
                      <Form.Select
                        value={filters.sortBy || 'created_at'}
                        onChange={(e) => updateFilters({ sortBy: e.target.value })}
                        size="sm"
                      >
                        <option value="created_at">{t("Registration Date")}</option>
                        <option value="full_name">{t("Name")}</option>
                        <option value="email">{t("Email")}</option>
                        <option value="current_status">{t("Status")}</option>
                        <option value="experience_years">{t("Experience")}</option>
                        <option value="current_ctc">{t("Current CTC")}</option>
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {/* Order */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t("Order")}</Form.Label>
                      <Form.Select
                        value={filters.order || 'DESC'}
                        onChange={(e) => updateFilters({ order: e.target.value })}
                        size="sm"
                      >
                        <option value="DESC">{t("Descending")}</option>
                        <option value="ASC">{t("Ascending")}</option>
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {/* Reset Filters Button */}
                  <div className="col-md-12 d-flex justify-content-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setFilters({
                          page: 1,
                          pageSize: 10,
                          search: null,
                          status: null,
                          applied_position: null,
                          is_blacklisted: null,
                          date_from: null,
                          date_to: null,
                          department_id: null,
                          sortBy: 'created_at',
                          order: 'DESC',
                        });
                        setSearchTerm('');
                      }}
                    >
                      <i className="fa fa-refresh me-2" />
                      {t("Reset All Filters")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card.Header>
          <Card.Body className="p-2 p-md-3">
            <div className="table-responsive">
              <Table
                className="table-striped"
                columns={columns}
                dataSource={candidates}
                loading={loading}
                rowKey={(record) => record.id}
                pagination={false}
              />
            </div>

            {/* Custom Pagination Outside Table - Left and Right Sides */}
            <div
              className="d-flex justify-content-between align-items-center mt-3 px-3 py-2"
              style={{
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
                borderRadius: "0 0 8px 8px",
                minHeight: "50px",
              }}
            >
              {/* LEFT SIDE - Total count */}
              <div className="d-flex align-items-center">
                <div
                  className="pagination-info-box"
                  style={{
                    background: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    color: "#666",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    fontWeight: "500",
                  }}
                >
                  {pagination.total > 0
                    ? `${
                        (pagination.current_page - 1) * pagination.per_page + 1
                      }-${Math.min(
                        pagination.current_page * pagination.per_page,
                        pagination.total
                      )} of ${pagination.total} candidates`
                    : "0 candidates"}
                </div>
              </div>

              {/* RIGHT SIDE - Pagination controls */}
              <div className="d-flex align-items-center gap-3">
                {/* Page size selector */}
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {pagination.per_page} / page
                  </span>

                  <select
                    className="form-select form-select-sm"
                    value={pagination.per_page}
                    onChange={(e) =>
                      updatePagination(1, parseInt(e.target.value))
                    }
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "6px 10px",
                      fontSize: "14px",
                      width: "110px",
                      height: "32px",
                    }}
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="d-flex align-items-center gap-1">
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                    disabled={pagination.current_page <= 1}
                    onClick={() =>
                      updatePagination(
                        pagination.current_page - 1,
                        pagination.per_page
                      )
                    }
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: "0",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  >
                    <i
                      className="fa fa-chevron-left"
                      style={{ fontSize: "12px" }}
                    ></i>
                  </button>

                  {Array.from(
                    { length: Math.min(5, pagination.total_pages) },
                    (_, i) => {
                      const startPage = Math.max(
                        1,
                        pagination.current_page - 2
                      );
                      const pageNum = startPage + i;

                      if (pageNum > pagination.total_pages) return null;

                      return (
                        <button
                          key={pageNum}
                          className={`btn btn-sm d-flex align-items-center justify-content-center ${
                            pageNum === pagination.current_page
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() =>
                            updatePagination(pageNum, pagination.per_page)
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            fontSize: "14px",
                            fontWeight:
                              pageNum === pagination.current_page
                                ? "600"
                                : "400",
                            border: "1px solid #d9d9d9",
                            borderRadius: "4px",
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                    disabled={pagination.current_page >= pagination.total_pages}
                    onClick={() =>
                      updatePagination(
                        pagination.current_page + 1,
                        pagination.per_page
                      )
                    }
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: "0",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  >
                    <i
                      className="fa fa-chevron-right"
                      style={{ fontSize: "12px" }}
                    ></i>
                  </button>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <CandidateModal
        show={modalOpen}
        onClose={handleModalClose}
        candidate={selectedCandidate}
        onSave={handleModalSave}
        submitting={modalSubmitting}
      />
    </div>
  );
};

export default CandidateList;
